/**
 * Version Management Utility
 *
 * Handles app version detection, comparison, and storage
 * Integrates with Vite build-time constants for version tracking
 */

/**
 * Version information structure stored in localStorage
 */
export interface VersionInfo {
  version: string;
  buildTimestamp: number;
  gitCommit: string;
  lastCheckedAt: number;
}

/**
 * Storage key for version information in localStorage
 */
const VERSION_STORAGE_KEY = 'chariot-app-version';

/**
 * Get the current version information from build-time constants
 *
 * @returns Current version info with timestamp
 */
export function getCurrentVersion(): VersionInfo {
  return {
    // eslint-disable-next-line no-undef
    version: __APP_VERSION__,
    // eslint-disable-next-line no-undef
    buildTimestamp: __BUILD_TIMESTAMP__,
    // eslint-disable-next-line no-undef
    gitCommit: __GIT_COMMIT__,
    lastCheckedAt: Date.now(),
  };
}

/**
 * Get the stored version information from localStorage
 *
 * @returns Stored version info or null if not found or invalid
 */
export function getStoredVersion(): VersionInfo | null {
  try {
    const stored = localStorage.getItem(VERSION_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored);
    if (!validateVersionInfo(parsed)) {
      console.warn('[Version] Invalid stored version format, clearing');
      localStorage.removeItem(VERSION_STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('[Version] Failed to retrieve stored version:', error);
    return null;
  }
}

/**
 * Update the stored version information in localStorage
 *
 * @param version - Version information to store
 */
export function updateStoredVersion(version: VersionInfo): void {
  try {
    localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(version));
  } catch (error) {
    // Handle quota exceeded or other storage errors
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.error(
        '[Version] localStorage quota exceeded, cannot store version'
      );
    } else {
      console.error('[Version] Failed to store version info:', error);
    }
  }
}

/**
 * Check if the app version has changed
 *
 * Compares current version against stored version
 * Returns true if version, build timestamp, or git commit differs
 *
 * @returns True if version has changed, false otherwise
 */
export function hasVersionChanged(): boolean {
  const currentVersion = getCurrentVersion();
  const storedVersion = getStoredVersion();

  // First-time user or no stored version
  if (!storedVersion) {
    return false;
  }

  // Check if any version identifier has changed
  return (
    storedVersion.version !== currentVersion.version ||
    storedVersion.buildTimestamp !== currentVersion.buildTimestamp ||
    storedVersion.gitCommit !== currentVersion.gitCommit
  );
}

/**
 * Validate version info structure
 *
 * Ensures the stored version data has the correct shape
 * Prevents injection or corruption issues
 *
 * @param info - Unknown object to validate
 * @returns True if valid VersionInfo structure
 */
function validateVersionInfo(info: unknown): info is VersionInfo {
  if (!info || typeof info !== 'object') {
    return false;
  }

  const version = (info as VersionInfo).version;
  const timestamp = (info as VersionInfo).buildTimestamp;
  const commit = (info as VersionInfo).gitCommit;
  const lastChecked = (info as VersionInfo).lastCheckedAt;

  return (
    typeof version === 'string' &&
    version.length > 0 &&
    typeof timestamp === 'number' &&
    timestamp > 0 &&
    typeof commit === 'string' &&
    commit.length > 0 &&
    typeof lastChecked === 'number' &&
    lastChecked > 0
  );
}
